import {FC, lazy, useState} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import './Reports.less';
import Icons from '../ui/IconButton/Icons';
import CategoryNav, {CategoryNavCategory} from '../ui/CategoryNav/CategoryNav';
import Auth from '../Auth/Auth';
import {LEGAL_ROLES} from '../Auth/roles';
import {getCurrentUserId} from '@/lib/picos/currentLogin';
import {ReportAuthProvider, useReportAuth} from './ReportAuthContext';
import Select from '../ui/Select/Select';
import type {UserRoles} from '@/@types/schema';
import type {SelectOption} from '@/@types/select';

// Lazy load report components
const TrustBalanceReport = lazy(() => import('@/Financial/TrustBalance/TrustBalanceReport'));
const ARAgingReport = lazy(() => import('./trust/ARAgingReport'));
const TrustLedgerReport = lazy(() => import('./trust/TrustLedgerReport'));
const TrustReceiptsReport = lazy(() => import('./trust/TrustReceiptsReport'));
const TrustDisbursementsReport = lazy(() => import('./trust/TrustDisbursementsReport'));
const TrustReconciliationReport = lazy(() => import('./trust/TrustReconciliationReport'));
const RevenueByResourceReport = lazy(() => import('./billing/RevenueByResourceReport'));
const InvoiceSummaryReport = lazy(() => import('./billing/InvoiceSummaryReport'));
const UnbilledTimeExpensesReport = lazy(() => import('./billing/UnbilledTimeExpensesReport'));
const FeeAllocationReport = lazy(() => import('./billing/FeeAllocationReport'));
const OriginatingAttorneyReport = lazy(() => import('./billing/OriginatingAttorneyReport'));
const PaymentHistoryReport = lazy(() => import('./payments/PaymentHistoryReport'));
const WriteOffReport = lazy(() => import('./billing/WriteOffReport'));
const BillingRealizationReport = lazy(() => import('./billing/BillingRealizationReport'));
const UtilizationReport = lazy(() => import('./productivity/UtilizationReport'));
const UserProductivityReport = lazy(() => import('./productivity/UserProductivityReport'));
const TimeByCategoryReport = lazy(() => import('./productivity/TimeByCategoryReport'));
const TimeByActivityTypeReport = lazy(() => import('./productivity/TimeByActivityTypeReport'));
const EstimationAccuracyReport = lazy(() => import('./productivity/EstimationAccuracyReport'));

// Marketing reports
const CampaignFlowReport = lazy(() => import('./marketing/CampaignFlowReport'));
const CampaignQualityReport = lazy(() => import('./marketing/CampaignQualityReport'));
const CampaignRoiReport = lazy(() => import('./marketing/CampaignRoiReport'));
const CampaignSpendReport = lazy(() => import('./marketing/CampaignSpendReport'));
const CampaignReturnsReport = lazy(() => import('./marketing/CampaignReturnsReport'));

// Risk reports
const LeadRedFlagsReport = lazy(() => import('./risk/LeadRedFlagsReport'));
const WinLossReport = lazy(() => import('./risk/WinLossReport'));
const WinLossDetailReport = lazy(() => import('./risk/WinLossDetailReport'));
const RecoveryRatesReport = lazy(() => import('./risk/RecoveryRatesReport'));

// Sales reports
const PipelineStagesReport = lazy(() => import('./sales/PipelineStagesReport'));
const PipelineFunnelReport = lazy(() => import('./sales/PipelineFunnelReport'));
const PipelineFlowReport = lazy(() => import('./sales/PipelineFlowReport'));

const ALL_ROLES: UserRoles[] = [
    'Owner', 'Administrator', 'Bookkeeper', 'Attorney', 'Paralegal',
    'Office Manager', 'Legal Secretary', 'Receptionist', 'Salesperson',
    'Marketing Manager', 'Human Resources',
];

const ROLE_OPTIONS: SelectOption<UserRoles, unknown>[] = ALL_ROLES.map(role => ({
    label: role,
    value: role,
}));

export const REPORT_CATEGORIES: CategoryNavCategory[] = [
    {
        id: 'trust',
        name: 'Trust & Compliance',
        icon: Icons.BANK,
        items: [
            {id: 'iolta-balance', label: 'IOLTA Balance', page: 'iolta-balance', icon: Icons.BANK, title: 'Required for bar compliance. Shows each client\'s trust balance as of a specific date, with the total matching your bank statement.', component: TrustBalanceReport},
            {id: 'ar-aging', label: 'AR Aging', page: 'ar-aging', icon: Icons.CLOCK, title: 'See which clients owe you money and for how long. Invoices are grouped by age to help you prioritize collection efforts.', component: ARAgingReport},
            {id: 'trust-ledger', label: 'Trust Ledger', page: 'trust-ledger', icon: Icons.LIST, title: 'Complete history of trust account activity for a specific client. Required documentation for bar audits.', component: TrustLedgerReport},
            {id: 'trust-receipts', label: 'Trust Receipts', page: 'trust-receipts', icon: Icons.RECEIPT, title: 'Journal of all money deposited into your trust account.', component: TrustReceiptsReport},
            {id: 'trust-disbursements', label: 'Trust Disbursements', page: 'trust-disbursements', icon: Icons.PAYOUT, title: 'Journal of all money paid out from your trust account.', component: TrustDisbursementsReport},
            {id: 'trust-reconciliation', label: 'Three-Way Reconciliation', page: 'trust-reconciliation', icon: Icons.MERGE, title: 'The gold standard for trust account compliance. Compares bank statement, checkbook register, and client balances.', component: TrustReconciliationReport},
        ],
    },
    {
        id: 'revenue',
        name: 'Revenue & Billing',
        icon: Icons.DOLLAR,
        items: [
            {id: 'revenue-by-resource', label: 'Revenue by Resource', page: 'revenue-by-resource', icon: Icons.USER, title: 'See how much revenue each attorney or staff member has generated.', component: RevenueByResourceReport},
            {id: 'invoice-summary', label: 'Invoice Summary', page: 'invoice-summary', icon: Icons.INVOICE, title: 'A complete list of all your invoices with payment status.', component: InvoiceSummaryReport},
            {id: 'unbilled-time-expenses', label: 'Unbilled Time & Expenses', page: 'unbilled-time-expenses', icon: Icons.CLOCK, title: 'Find money you\'ve earned but haven\'t billed yet.', component: UnbilledTimeExpensesReport},
            {id: 'fee-allocation', label: 'Fee Allocation', page: 'fee-allocation', icon: Icons.DOLLAR, title: 'Track revenue by performing attorney.', component: FeeAllocationReport},
            {id: 'originating-attorney', label: 'Originating Attorney Revenue', page: 'originating-attorney', icon: Icons.USER, title: 'See total revenue from matters each attorney brought into the firm.', component: OriginatingAttorneyReport},
            {id: 'payment-history', label: 'Payment History', page: 'payment-history', icon: Icons.CREDIT_CARD, title: 'Track all electronic payments processed through Outlaw Payments.', component: PaymentHistoryReport},
            {id: 'write-off', label: 'Write-Off Report', page: 'write-off', icon: Icons.CALCULATOR, title: 'Track discounts and amounts written off.', component: WriteOffReport},
            {id: 'billing-realization', label: 'Billing Realization', page: 'billing-realization', icon: Icons.PERCENTAGE, title: 'Compare value of work performed to amount actually billed. Industry benchmark is 88%+.', component: BillingRealizationReport},
            {id: 'recovery-rates', label: 'Recovery Rates', page: 'recovery-rates', icon: Icons.ANALYTICS, title: 'See how red flags impact your ability to collect revenue.', component: RecoveryRatesReport},
        ],
    },
    {
        id: 'productivity',
        name: 'Productivity',
        icon: Icons.CLOCK,
        items: [
            {id: 'utilization', label: 'Utilization Rate', page: 'utilization', icon: Icons.CIRCLE_USER_CLOCK, title: 'See how much of each person\'s time is spent on billable work vs administrative tasks.', component: UtilizationReport},
            {id: 'user-productivity', label: 'User Productivity', page: 'user-productivity', icon: Icons.USER_CLOCK, title: 'Detailed hours breakdown by person over time.', component: UserProductivityReport},
            {id: 'time-by-category', label: 'Time by Category', page: 'time-by-category', icon: Icons.LIST, title: 'See how time is distributed across different types of work.', component: TimeByCategoryReport},
            {id: 'time-by-activity-type', label: 'Time by Activity Type', page: 'time-by-activity-type', icon: Icons.TASKS, title: 'Breakdown of hours by specific activity types.', component: TimeByActivityTypeReport},
            {id: 'estimation-accuracy', label: 'Estimation Accuracy', page: 'estimation-accuracy', icon: Icons.STOPWATCH, title: 'Compare estimated vs actual time for completed tasks.', component: EstimationAccuracyReport},
        ],
    },
    {
        id: 'sales',
        name: 'Sales',
        icon: Icons.LEAD,
        items: [
            {id: 'pipeline-stages', label: 'Pipeline Stages', page: 'pipeline-stages', icon: Icons.PIPELINE_STAGES, title: 'See how many leads are sitting in each stage of your sales pipeline. Switch between counts and estimated dollar value to spot bottlenecks.', component: PipelineStagesReport},
            {id: 'pipeline-funnel', label: 'Pipeline Funnel', page: 'pipeline-funnel', icon: Icons.PIPELINE_FUNNEL, title: 'See your sales pipeline as a sequential funnel from first contact through retainer funded, to identify where conversion drops off.', component: PipelineFunnelReport},
            {id: 'pipeline-flow', label: 'Pipeline Flow', page: 'pipeline-flow', icon: Icons.PIPELINE_FLOW, title: 'See the real-world paths leads take through your pipeline (skipped stages, loops, early exits) as a Sankey flow diagram.', component: PipelineFlowReport},
            {id: 'lead-red-flags', label: 'Lead Red Flags', page: 'lead-red-flags', icon: Icons.FLAG, title: 'See how red flags on leads relate to the campaigns that generated them.', component: LeadRedFlagsReport},
            {id: 'win-loss', label: 'Win/Loss', page: 'win-loss', icon: Icons.YIN_YANG, title: 'See how red flags on leads correlate with winning or losing the case.', component: WinLossReport},
            {id: 'win-loss-detail', label: 'Win/Loss Detail', page: 'win-loss-detail', icon: Icons.LIST, title: 'Detailed breakdown of lead outcomes by red flag.', component: WinLossDetailReport},
        ],
    },
    {
        id: 'client',
        name: 'Client & Matter',
        icon: Icons.CONTACT,
        items: [],
    },
    {
        id: 'marketing',
        name: 'Marketing',
        icon: Icons.MARKETING,
        items: [
            {id: 'campaign-flow', label: 'Campaign Flow', page: 'campaign-flow', icon: Icons.NETWORK, title: 'Visualize how leads flow through your marketing pipeline. Shows the path from initial contact through won, lost, or other outcomes.', component: CampaignFlowReport},
            {id: 'campaign-quality', label: 'Campaign Quality', page: 'campaign-quality', icon: Icons.MARKETING, title: 'See why leads from each campaign were refused, broken down by case type.', component: CampaignQualityReport},
            {id: 'campaign-roi', label: 'Campaign ROI', page: 'campaign-roi', icon: Icons.ARROW_TREND_UP, title: 'Compare the return on investment for each marketing campaign.', component: CampaignRoiReport},
            {id: 'campaign-spend', label: 'Campaign Spend', page: 'campaign-spend', icon: Icons.DOLLAR, title: 'See how your marketing budget is distributed across campaigns.', component: CampaignSpendReport},
            {id: 'campaign-returns', label: 'Campaign Returns', page: 'campaign-returns', icon: Icons.HAND_HOLDING_DOLLAR, title: 'See how much revenue has been collected from leads generated by each campaign.', component: CampaignReturnsReport},
        ],
    },
    {
        id: 'operations',
        name: 'Operations',
        icon: Icons.ADMIN,
        items: [],
    },
];

// Roles that can access each report. Owner and Administrator always have access (handled in canAccessReport).
// LEGAL_ROLES is the canonical list from Auth/roles (Paralegal, Attorney, Legal Secretary).
const SALES_MARKETING_ROLES: UserRoles[] = ['Salesperson', 'Marketing Manager', 'Office Manager'];

export const REPORT_ACCESS: Record<string, UserRoles[]> = {
    // Trust & Compliance
    'iolta-balance':        ['Bookkeeper'],
    'ar-aging':             ['Bookkeeper', ...LEGAL_ROLES, 'Office Manager'],
    'trust-ledger':         ['Bookkeeper', ...LEGAL_ROLES],
    'trust-receipts':       ['Bookkeeper'],
    'trust-disbursements':  ['Bookkeeper'],
    'trust-reconciliation': ['Bookkeeper'],

    // Revenue & Billing
    'revenue-by-resource':    ['Bookkeeper', ...LEGAL_ROLES],
    'invoice-summary':        ['Bookkeeper', ...LEGAL_ROLES, 'Office Manager'],
    'unbilled-time-expenses': ['Bookkeeper', ...LEGAL_ROLES, 'Office Manager'],
    'fee-allocation':         ['Bookkeeper', ...LEGAL_ROLES],
    'originating-attorney':   ['Bookkeeper', 'Attorney'],
    'payment-history':        ['Bookkeeper', 'Office Manager'],
    'write-off':              ['Bookkeeper'],
    'billing-realization':    ['Bookkeeper', ...LEGAL_ROLES],
    'recovery-rates':         [...LEGAL_ROLES, 'Bookkeeper'],

    // Productivity
    'utilization':          [...LEGAL_ROLES, 'Office Manager'],
    'user-productivity':    [...LEGAL_ROLES, 'Office Manager'],
    'time-by-category':     [...LEGAL_ROLES, 'Office Manager'],
    'time-by-activity-type': [...LEGAL_ROLES, 'Office Manager'],
    'estimation-accuracy':  [...LEGAL_ROLES, 'Office Manager'],

    // Sales (Owner + Administrator always have access via canAccessReport)
    'pipeline-stages':  SALES_MARKETING_ROLES,
    'pipeline-funnel':  SALES_MARKETING_ROLES,
    'pipeline-flow':    SALES_MARKETING_ROLES,
    'lead-red-flags':   SALES_MARKETING_ROLES,
    'win-loss':         SALES_MARKETING_ROLES,
    'win-loss-detail':  SALES_MARKETING_ROLES,

    // Marketing (Owner + Administrator always have access via canAccessReport)
    'campaign-flow':    SALES_MARKETING_ROLES,
    'campaign-quality': SALES_MARKETING_ROLES,
    'campaign-roi':     SALES_MARKETING_ROLES,
    'campaign-spend':   SALES_MARKETING_ROLES,
    'campaign-returns': SALES_MARKETING_ROLES,
};

function filterCategoriesByRole(
    categories: CategoryNavCategory[],
    canAccess: (reportId: string) => boolean,
): CategoryNavCategory[] {
    return categories
        .map(category => ({
            ...category,
            items: category.items.filter(item => canAccess(item.id)),
        }))
        .filter(category => category.items.length > 0);
}

const ReportsContent: FC = () => {
    const auth = useReportAuth();

    const canAccessReport = (reportId: string): boolean => {
        if (auth.isAdmin()) return true;
        const allowedRoles = REPORT_ACCESS[reportId];
        if (!allowedRoles) return false;
        return auth.hasRoles(allowedRoles, false);
    };

    // Recomputed every render, not memoized: the role checks read the store's
    // roles, which can arrive after mount (OWA-1005). ReportAuthProvider
    // re-renders on every roles change, so this re-filters when they land.
    const visibleCategories = filterCategoriesByRole(REPORT_CATEGORIES, canAccessReport);

    if (visibleCategories.length === 0) {
        return (
            <div className="reports">
                <div className="reports__unauthorized">
                    <FontAwesomeIcon icon={Icons.LOCK.reactIcon} className="reports__unauthorized-icon" />
                    <h2>Access Restricted</h2>
                    <p>You don't have access to any reports. Contact your firm administrator if you need access.</p>
                </div>
            </div>
        );
    }

    return (
        <CategoryNav
            basePath="/reports"
            cacheKey="reports-page"
            categories={visibleCategories}
            emptyMessage="Select a report from the list"
            className="reports"
        />
    );
};

const Reports: FC = () => {
    const [overrideRoles, setOverrideRoles] = useState<UserRoles[] | null>(null);

    // Real auth check for showing dev toolbar - only real admins/owners can use it
    const userId = getCurrentUserId();
    const isRealAdmin = userId ? Auth.isAdmin(userId) : false;
    const isDev = import.meta.env.MODE === 'development';
    const showDevToolbar = isDev && isRealAdmin;

    const handleRoleSelectChange = (selected: SelectOption<UserRoles, unknown> | SelectOption<UserRoles, unknown>[]) => {
        if (Array.isArray(selected)) {
            setOverrideRoles(selected.length > 0 ? selected.map(s => s.value) : []);
        }
    };

    const handleClearOverride = () => {
        setOverrideRoles(null);
    };

    const isOverrideActive = overrideRoles !== null;

    return (
        <ReportAuthProvider overrideRoles={overrideRoles} setOverrideRoles={setOverrideRoles}>
            {showDevToolbar && (
                <div className={`reports-dev-toolbar${isOverrideActive ? ' reports-dev-toolbar--active' : ''}`}>
                    <div className="reports-dev-toolbar__label">
                        DEV Role Override
                    </div>
                    <div className="reports-dev-toolbar__select">
                        <Select
                            name="dev-role-override"
                            options={ROLE_OPTIONS}
                            isMulti
                            allowNone
                            placeholder="No override (using real roles)"
                            onChange={handleRoleSelectChange}
                            defaultValue={overrideRoles ? ROLE_OPTIONS.filter(o => overrideRoles.includes(o.value)) : []}
                        />
                    </div>
                    {isOverrideActive && (
                        <button
                            className="reports-dev-toolbar__clear"
                            onClick={handleClearOverride}
                            type="button"
                        >
                            Clear
                        </button>
                    )}
                    {isOverrideActive && (
                        <span className="reports-dev-toolbar__status">
                            Simulating: {overrideRoles.length === 0 ? 'No roles' : overrideRoles.join(', ')}
                        </span>
                    )}
                </div>
            )}
            <ReportsContent />
        </ReportAuthProvider>
    );
};

export default Reports;
